import { Controller } from "@hotwired/stimulus"
import { trackEvent } from "../utils/analytics"

// Fires one GA4 event. Attach it wherever a step in the funnel happens.
//
// Until this existed the only things GA4 knew were pageviews and the
// server-side purchase, so "someone clicked an ad — where did they go, and
// where did we lose them?" had no answer at all. The events below are the
// standard GA4 ecommerce funnel names on purpose: GA4 builds its funnel
// exploration and the Ads conversion import around exactly these, so using
// custom names would mean rebuilding reports by hand.
//
//   view_item_list    a suburb page rendered its instructor deck
//   select_item       someone tapped through to an instructor's profile
//   view_availability someone opened an instructor's calendar from a card
//   select_slot       someone picked a time in that calendar (skips the profile)
//   view_item         an instructor profile was viewed
//   begin_checkout    the booking wizard was entered
//   add_payment_info  the payment step was reached
//   purchase          already reported server-side — not fired here
//
// The pack-first checkout's own steps, custom names because GA4 has none for
// them. Fired from markup with this controller unless noted:
//
//   amount_chosen        the Hours step was left with an amount (hours, and
//                        hours_variant: the HoursStepTest half or slot_tap)
//   test_package_view    the test package screen rendered
//   test_package_choice  its button: the package added or skipped
//   payment_attempt      Pay asked Stripe to charge (stripe_checkout_controller)
//   payment_error        Pay was refused or the charge failed, with Stripe's
//                        reason code (stripe_checkout_controller)
//
// Two ways to fire:
//
//   on click   data-controller="analytics"
//              data-analytics-event-value="select_item"
//              data-action="click->analytics#fire"
//
//   on render  data-controller="analytics"
//              data-analytics-event-value="view_item_list"
//              data-analytics-on-connect-value="true"
//
//   on sight   data-controller="analytics"
//              data-analytics-event-value="view_item_list"
//              data-analytics-on-visible-value="true"
//
// on sight is for a block that is not on screen when the page loads (the
// practice-test panel stacks under the quiz below 1024px): fired on render it
// counted every page load as a view, including learners who never scrolled
// to it. It fires when half the block is in the viewport; add
// data-analytics-once-value="true" to stop a Back restore counting it again.
//
// Extra event parameters ride along as JSON in data-analytics-params-value.
export default class extends Controller {
  static values = {
    event: String,
    params: { type: Object, default: {} },
    onConnect: { type: Boolean, default: false },
    onVisible: { type: Boolean, default: false },
    once: { type: Boolean, default: false },
  }

  declare readonly eventValue: string
  declare readonly paramsValue: Record<string, unknown>
  declare readonly onConnectValue: boolean
  declare readonly onVisibleValue: boolean
  declare readonly onceValue: boolean

  private observer: IntersectionObserver | null = null

  connect(): void {
    if (this.onVisibleValue) {
      this.observeVisibility()
      return
    }
    if (!this.onConnectValue) return

    // Turbo Drive paints a cached preview before the fresh response arrives,
    // and both connect this controller — so a view step would be counted twice
    // for one navigation, making every downstream ratio look worse than it is.
    // Only the preview render carries this attribute.
    //
    // A "fired once" instance flag does NOT solve this: Stimulus disconnects
    // and reconnects around the swap, so the flag resets. Verified by firing a
    // double append in a browser and watching it count twice.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.fire()

    // once: Turbo Drive caches the page and restores that snapshot on Back,
    // reconnecting this controller — a server-side "already reported" flag
    // cannot see that. Drop the trigger from the live DOM so the snapshot
    // Turbo takes carries no on-connect, and the restore stays silent. Only
    // for events that are one-per-journey (begin_checkout); a view_item on a
    // profile the learner comes back to is a view again.
    if (this.onceValue) this.element.removeAttribute(`data-${this.identifier}-on-connect-value`)
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.observer = null
  }

  fire(): void {
    trackEvent(this.eventValue, this.paramsValue)
  }

  // The same preview guard as on-connect: Turbo's cached preview is not a page
  // anyone looked at. Without IntersectionObserver (very old browsers) it
  // falls back to firing on render, which is what it did before.
  private observeVisibility(): void {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    if (!("IntersectionObserver" in window)) {
      this.fire()
      return
    }

    this.observer = new IntersectionObserver((entries) => {
      // The ratio is checked as well as isIntersecting: browsers that follow
      // the spec's looser meaning (any overlap at all) report a sliver as
      // intersecting, which would count a panel nobody has seen.
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return

      this.observer?.disconnect()
      this.observer = null
      this.fire()
      // once: the same Back-restore problem as on-connect. Drop the trigger so
      // the snapshot Turbo restores on Back stays silent.
      if (this.onceValue) this.element.removeAttribute(`data-${this.identifier}-on-visible-value`)
    }, { threshold: 0.5 })
    this.observer.observe(this.element)
  }
}
